import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Color } from '../../utils/constants'
import Separator from '../../components/Separator'
import HeaderBody from './components/HeaderBody'
import { twMerge } from 'tailwind-merge'
import HeaderContext from './components/HeaderContext'

interface HeaderProps {
  setMarginHeader: (value: number) => void
}

const Header = ({ setMarginHeader }: HeaderProps) => {
    const initialColors = {
        text: 'text-white',
        fill: 'fill-white',
        color: Color.WHITE,
    }
    const [colors, setColors] = useState(initialColors)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const [showScrollUpMenu, setShowScrollUpMenu] = useState(false)
    const [scrollY, setScrollY] = useState(window.scrollY)
    const { headerColor, headerHeight } = useContext(HeaderContext)
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (isMobileMenuOpen || [Color.PURPLE, Color.BLACK].includes(headerColor)) {
            setColors({ text: 'text-white', fill: 'fill-white', color: Color.WHITE })
        } else {
            setColors({ text: 'text-black', fill: 'fill-black', color: Color.BLACK })
        }
    }, [headerColor, isMobileMenuOpen])

    const separatorRef = useRef<HTMLDivElement>(null)

    const { pathname } = useLocation()
    useEffect(() => {
        setIsMobileMenuOpen(false)
    }, [pathname])

    const scrollHandler = useCallback(() => {
        const currentScrollPos = window.scrollY
        setShowScrollUpMenu(scrollY > currentScrollPos)
        setScrollY(currentScrollPos)
    }, [scrollY])

    useEffect(() => {
        window.addEventListener('scroll', scrollHandler)
        return () => {
            window.removeEventListener('scroll', scrollHandler)
        }
    }, [scrollHandler])

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.height = '100vh'
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.height = 'auto'
            document.body.style.overflow = 'auto'
        }
    }, [isMobileMenuOpen])

    useEffect(() => {
        const calculateHeight = () => {
            requestAnimationFrame(() => {
                if (ref.current) {
                    const height = ref.current.clientHeight + (scrollY === 0 || isMobileMenuOpen ? 2 : 20)
                    setMarginHeader(height)
                }
            })
        }
        calculateHeight()
        window.addEventListener('resize', calculateHeight)
        return () => {
            window.removeEventListener('resize', calculateHeight)
        }
    }, [ref, scrollY, isMobileMenuOpen, setMarginHeader])

    const getMobileMenuClass = () => {
        switch (headerColor) {
        case Color.PURPLE:
            return 'mobile-purple-background'
        case Color.GREEN_LIGHT:
        case Color.GREEN:
            return 'mobile-green-background'
        default:
            return 'mobile-sand-background'
        }
    }

    return (
        <header
            className={twMerge(
                'w-full side-padding py-3 z-40 fixed header-scroll-from-top',
                isMobileMenuOpen && 'h-screen ' + getMobileMenuClass(),
                scrollY !== 0 && 'bg-black opacity-0 header-scroll-from-bottom',
                (showScrollUpMenu || isMobileMenuOpen) && 'opacity-100',
            )}
            style={{ top: showScrollUpMenu || scrollY === 0 || isMobileMenuOpen ? 0 : -headerHeight }}
            ref={ref}
        >
            <HeaderBody
                colors={showScrollUpMenu && scrollY !== 0 ? initialColors : colors}
                isMobileMenuOpen={isMobileMenuOpen}
                setIsMobileMenuOpen={setIsMobileMenuOpen}
            />
            {(scrollY === 0 || isMobileMenuOpen) && (
                <Separator
                    separatorColor={colors.color}
                    className={twMerge('mt-3 relative z-30')}
                    separatorRef={separatorRef}
                    id='header-separator'
                />
            )}
        </header>
    )
}

export default Header
